import React from "react"
import {Field, FormRenderProps} from 'react-final-form'
import {registerPlateFormatter, validate} from "../Utils/helpers"
import { FieldRenderProps } from "react-final-form"
import { IconButton, Typography, TextFieldProps } from "@mui/material"
import { Clear } from "@mui/icons-material"
import registerPlateStars from "../Static/european_stars.svg"
import {makeStyles} from "tss-react/mui";
import InputWrapper from "./Form/Input";
import {Theme} from "@mui/material/styles";
import {RegisterSubmitEvent} from "../Containers/RootContainer";
import {ReservationState} from "../Redux/reservationRedux";
import {useTranslation} from "react-i18next";
import TextFieldWrapper from './Form/TextField'

const useStyles = makeStyles<number>()((theme: Theme, scale: number = 1) => ({
    errorText: {
        color: theme.palette.error.main,
    },
    landText: {
        color: '#fff',
        fontWeight: theme.typography.fontWeightBold,
    },
    registerPlate: {
        backgroundColor: '#fff',
        borderRadius: 8 * scale,
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: scale,
        borderStyle: 'solid',
        borderColor: '#000',
        height: 80 * scale,
        maxWidth: 350 * scale,
        boxShadow: theme.shadows[4],
        [theme.breakpoints.down('sm')]: {
            marginBottom: theme.spacing(3 * scale),
            marginLeft: 0,
            marginRight: 0,
        },
    },
    registerPlateBlue: {
        height: 80 * scale,
        paddingLeft: theme.spacing(scale),
        paddingRight: theme.spacing(scale),
        borderBottomLeftRadius: 8 * scale,
        borderTopLeftRadius: 8 * scale,
        borderWidth: 0,
        borderTopWidth: scale,
        borderBottomWidth: scale,
        borderStyle: 'solid',
        borderColor: '#000',
        backgroundColor: '#3333FF',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-evenly',
        alignItems: 'center',
    },
    registerPlateContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    registerStars: {
        width: 'auto',
        height: 45 * scale,
        marginTop: theme.spacing(scale),
        [theme.breakpoints.down('sm')]: {
            width: '60%',
        },
    },
    registerTextField: {
        margin: theme.spacing(scale),
        backgroundColor: '#fff',
        height: '100%',
        width: '100%',
    },
    registerTextFieldInput: {
        color: '#333',
        fontFamily: theme.typography.caption.fontFamily,
        fontSize: theme.typography.pxToRem(50 * scale),
        fontWeight: theme.typography.fontWeightBold,
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            fontSize: theme.typography.pxToRem(30 * scale),
        },
        ['@media (max-width:360px)']: {
            fontSize: theme.typography.pxToRem(22 * scale),
        },
    },
    clearButton: {
        opacity: 0.5,
        fontSize: theme.typography.pxToRem(40 * scale),
    },
    textField: {
        width: 250 * scale,
        paddingRight: '0 !important',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
}))

interface RegisterFormProps
    extends FormRenderProps<RegisterSubmitEvent, Partial<RegisterSubmitEvent>> {
    registerInfo?: ReservationState['registerInfo']
    fetching: boolean
    renderSubmitRegisterPlate: () => JSX.Element
    inputId: string
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
    scale?: number
}

interface RegisterNumberFieldProps {
    id: string;
    disabled: boolean;
    textFieldProps?: TextFieldProps
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    scale?: number
    showClearButton?: boolean
    showError?: boolean
    showStars?: boolean
}

const registerPlateRegex = /^[0-9a-zA-ZÄÖÅäöå]{1,4}-[0-9a-zA-ZÄÖÅäöå]{1,4}$/gis

const validateRegisterNumber = (str: string) => {
    return validate(registerPlateFormatter(str), registerPlateRegex)
}

const RegisterNumberField: React.FC<RegisterNumberFieldProps> = ({ id, disabled, onChange, scale, showError, showClearButton, showStars}) => {
    const { t } = useTranslation()

    const validator = {
        register: (item: string | null) => {
            if (!item || item.trim() === '') {
                return t('errors.required');
            }
            return !validateRegisterNumber(item) ? t('errors.register') : '';
        }
    }

    return (
        <Field
            id={id}
            name="registerNumber"
            type="text"
            validate={validator.register}
            format={registerPlateFormatter}
        >
            {(props) => <RegisterFormInput {...props} disabled={disabled} onChange={onChange} scale={scale} showError={showError} showClearButton={showClearButton} showStars={showStars}/>}
        </Field>
    );
};

export const RegisterNumberFieldSimple: React.FC<RegisterNumberFieldProps> = ({ id, disabled, onChange, textFieldProps, scale, showClearButton, showStars}) => {
    const { t } = useTranslation()

    const validator = {
        register: (item: string | null) => {
            if (!item || item.trim() === '') {
                return t('errors.required');
            }
            return !validateRegisterNumber(item) ? t('errors.register') : '';
        }
    }

    return (
        <Field
            id={id}
            name="registerNumber"
            type="text"
            validate={validator.register}
            format={registerPlateFormatter}
        >
            {(props) => <RegisterFormInputSimple {...props} disabled={disabled} textFieldProps={textFieldProps} onChange={onChange} scale={scale} showClearButton={showClearButton} showStars={showStars}/>}
        </Field>
    );
};

export const RegisterFormInput = (props: FieldRenderProps<any> & Partial<RegisterFormProps>) => {
    const { showClearButton, showError, showStars, inputStyle, ...restProps } = props
    const { scale, meta, disabled, input, onChange } = restProps
    const { classes } = useStyles(scale ?? 1)
    const showErrorMessage = showError && !meta.active && meta.error && meta.touched

    return (
        <div className={classes.registerPlateContainer}>
            <div className={classes.registerPlate}>
                {showStars ? <div className={classes.registerPlateBlue}>
                    <img
                        src={registerPlateStars}
                        className={classes.registerStars}
                        alt="Rekisterikilven tähdet"
                    />
                    <Typography className={classes.landText}>FIN</Typography>
                </div> : null}
                <InputWrapper
                    aria-label="Rekisterinumerolla haku"
                    {...restProps}
                    disabled={disabled}
                    classes={{
                        root: classes.registerTextField,
                    }}
                    inputProps={{
                        autoFocus: true,
                        className: classes.registerTextFieldInput,
                        style: inputStyle,
                    }}
                    disableUnderline
                    endAdornment={showClearButton ? <IconButton size="large" onClick={() => input.onChange('')}>
                        <Clear className={classes.clearButton} />
                    </IconButton> : null}
                    placeholder="ABC-123"
                    input={{...input, onChange: (e) => {
                        e.target.value = registerPlateFormatter(e.target.value ?? '')
                        input.onChange(e)
                        if(onChange) {
                            onChange(e)
                        }
                    }}}
                />
            </div>
            {showErrorMessage && (
                <Typography display="inline" className={classes.errorText}>
                    {props.meta.error}
                </Typography>
            )}
        </div>
    )
}

export const RegisterFormInputSimple = (props: FieldRenderProps<any> & Partial<RegisterFormProps>) => {
    const { showClearButton, showError, showStars, textFieldProps, ...restProps } = props
    const { scale, meta, disabled, input, onChange } = restProps
    const { classes } = useStyles(scale ?? 1)

    return (
        <TextFieldWrapper
            aria-label="Rekisterinumerolla haku"
            {...restProps}
            {...textFieldProps}
            disabled={disabled}
            placeholder="ABC-123"
            InputProps={{
                endAdornment: showClearButton ? <IconButton size="large" onClick={() => input.onChange('')}>
                    <Clear className={classes.clearButton} />
                </IconButton> : null
            }}
            input={{...input, onChange: (e) => {
                    e.target.value = registerPlateFormatter(e.target.value ?? '')
                    input.onChange(e)
                    if(onChange) {
                        onChange(e)
                    }
                }}}
        />
    )
}

export default RegisterNumberField;
